import { useState, useEffect, useRef } from "react"
import { Box, Paper, Typography, IconButton, TextField, Divider } from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import CheckIcon from "@mui/icons-material/Check"
import { useApiService } from "../hooks/Api/useApiService"
import T from "../hooks/Language/T"
import useTranslationSharie from "../hooks/Language/useTranslation"
import useError from "../hooks/Error/useError"

const Chat = ({ to, skill_name }) => {
  const [messages, setMessages] = useState([])
  const [shouldFetch, setShouldFetch] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const { postData } = useApiService()
  const t = useTranslationSharie()
  const { Error: AlertError } = useError()
  const messagesEndRef = useRef(null)

  useEffect(() => {
    postData("sharie/getmessages", {
      from: localStorage.getItem("user"),
      to,
      skill: skill_name,
    })
      .then((data) => setMessages(data.messages))
      .catch((err) => console.error(err))
  }, [to, shouldFetch, skill_name])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sendMessage = async (e) => {
    if ((e.ctrlKey && e.key === "Enter") || e === "send_icon") {
      postData("sharie/addmessage", {
        from: localStorage.getItem("user"),
        message: inputValue,
        to,
        skill: skill_name,
      })
        .then((data) => {
          setMessages(data.messages)
          setInputValue("")
          setShouldFetch(!shouldFetch)
        })
        .catch((err) => console.error(err))
    }
  }

  const isOwnMessage = (msg) => msg.from === localStorage.getItem("user")

  return (
    <T>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "90vh",
          width: "100vw",
          margin: "0 auto",
          borderRadius: 2,
          boxShadow: 3,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            background: "linear-gradient(180deg,#f5f7fb,#ffffff)",
          }}
        >
          {messages?.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                justifyContent: isOwnMessage(msg) ? "flex-end" : "flex-start",
                mb: 1,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 1.5,
                  maxWidth: "70%",
                  borderRadius: 3,
                  background: isOwnMessage(msg)
                    ? "linear-gradient(135deg, #7e57c2, #2196f3)"
                    : "#f1f1f1",
                  color: isOwnMessage(msg) ? "white" : "black",
                  position: "relative",
                }}
              >
                <Typography variant='body1'>{msg.message}</Typography>
                {msg.seen && isOwnMessage(msg) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      mt: 0.5,
                    }}
                  >
                    <CheckIcon fontSize='small' sx={{ opacity: 0.7 }} />
                    <CheckIcon fontSize='small' sx={{ opacity: 0.7, ml: -1 }} />
                  </Box>
                )}
              </Paper>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            bgcolor: "text.primary",
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            variant='outlined'
            placeholder={t("Type your message...")}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => sendMessage(e)}
            sx={{ mr: 1 }}
          />
          <IconButton
            color='primary'
            onClick={() => sendMessage("send_icon")}
            sx={{
              bgcolor: "primary.main",
              color: "text.primary",
              "&:hover": { bgcolor: "secondary.main" },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>

        <AlertError />
      </Box>
    </T>
  )
}

export default Chat
